import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAdminScope } from '../hooks/useAdminScope';
import { canEditDepartments } from '../utils/adminAccess';
import {
  adminDepartmentsApi,
  RequestError,
  type DepartmentResponse,
} from '../api/client';

type EditRow = {
  name: string;
  code: string;
  status: string;
};

type ForceDeleteConfirm = {
  target: DepartmentResponse;
  userCount: number | null;
};

const STATUS_OPTIONS = ['active', 'inactive'] as const;

/**
 * 削除エラーから user_count を抜き出す。
 * BE は 409 + error_code="DEPARTMENT_HAS_USERS" + details.user_count を返す想定
 */
function extractUserCount(err: unknown): number | null {
  if (!(err instanceof RequestError)) return null;
  const details = err.details;
  if (details && typeof details === 'object') {
    const value = (details as Record<string, unknown>).user_count;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  // body 側に直接含まれることもあるため両方確認する
  const body = err.body;
  if (body && typeof body === 'object') {
    const value = (body as Record<string, unknown>).user_count;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

/** 部署マスタ管理（CRUD）— 2026-05-30 追加 / 課題管理表 #4 */
export default function AdminDepartments() {
  const { t } = useTranslation();
  const adminScope = useAdminScope();
  const editable = canEditDepartments(adminScope);
  const { getToken } = useAuth();

  const [list, setList] = useState<DepartmentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  // 新規作成フォーム
  const [createName, setCreateName] = useState('');
  const [createCode, setCreateCode] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [createMessage, setCreateMessage] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // 行ごとの編集状態 / 保存メッセージ
  const [editRows, setEditRows] = useState<Record<string, EditRow>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [rowNotice, setRowNotice] = useState<Record<string, string>>({});

  // 強制削除確認ダイアログ
  const [forceConfirm, setForceConfirm] = useState<ForceDeleteConfirm | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    adminDepartmentsApi
      .list(token, { include_inactive: includeInactive })
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        setList(rows);
        setEditRows(
          rows.reduce<Record<string, EditRow>>((acc, row) => {
            acc[row.id] = {
              name: row.name ?? '',
              code: row.code ?? '',
              status: row.status ?? 'active',
            };
            return acc;
          }, {})
        );
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('common.error'));
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [getToken, includeInactive, t]);

  useEffect(() => {
    load();
  }, [load]);

  const sortedList = useMemo(
    () => [...list].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'ja-JP')),
    [list]
  );

  const clearRowMessages = (id: string) => {
    setRowError((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setRowNotice((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    setCreateError(null);
    setCreateMessage(null);
    const name = createName.trim();
    if (name === '') {
      setCreateError(t('admin.departments.nameRequired'));
      return;
    }
    setCreating(true);
    const code = createCode.trim();
    adminDepartmentsApi
      .create({ name, code: code === '' ? null : code }, token)
      .then(() => {
        setCreateMessage(t('admin.departments.createSuccess'));
        setCreateName('');
        setCreateCode('');
        load();
      })
      .catch((err) => {
        setCreateError(err instanceof Error ? err.message : t('common.error'));
      })
      .finally(() => setCreating(false));
  };

  const handleUpdate = (row: DepartmentResponse) => {
    const token = getToken();
    if (!token) return;
    const edit = editRows[row.id];
    if (!edit) return;
    clearRowMessages(row.id);
    const name = edit.name.trim();
    if (name === '') {
      setRowError((prev) => ({
        ...prev,
        [row.id]: t('admin.departments.nameRequired'),
      }));
      return;
    }
    setSavingId(row.id);
    adminDepartmentsApi
      .update(
        row.id,
        {
          name,
          code: edit.code.trim() === '' ? null : edit.code.trim(),
          status: edit.status,
        },
        token
      )
      .then(() => {
        setRowNotice((prev) => ({ ...prev, [row.id]: t('admin.departments.updateSuccess') }));
        load();
      })
      .catch((err) => {
        setRowError((prev) => ({
          ...prev,
          [row.id]: err instanceof Error ? err.message : t('common.error'),
        }));
      })
      .finally(() => setSavingId(null));
  };

  /**
   * 削除（論理削除）。
   * まず force=false で実行し、409 DEPARTMENT_HAS_USERS が返れば
   * user_count を確認ダイアログに表示して force=true で再実行を求める
   */
  const handleDelete = (row: DepartmentResponse) => {
    const token = getToken();
    if (!token) return;
    if (!window.confirm(t('admin.departments.deleteConfirm', { name: row.name }))) return;
    clearRowMessages(row.id);
    setDeletingId(row.id);
    adminDepartmentsApi
      .delete(row.id, token, { force: false })
      .then(() => {
        setRowNotice((prev) => ({ ...prev, [row.id]: t('admin.departments.deleteSuccess') }));
        load();
      })
      .catch((err) => {
        if (err instanceof RequestError && err.errorCode === 'DEPARTMENT_HAS_USERS') {
          setForceConfirm({ target: row, userCount: extractUserCount(err) });
        } else {
          setRowError((prev) => ({
            ...prev,
            [row.id]: err instanceof Error ? err.message : t('common.error'),
          }));
        }
      })
      .finally(() => setDeletingId(null));
  };

  const handleForceDelete = () => {
    if (!forceConfirm) return;
    const token = getToken();
    if (!token) return;
    const target = forceConfirm.target;
    setDeletingId(target.id);
    adminDepartmentsApi
      .delete(target.id, token, { force: true })
      .then(() => {
        setRowNotice((prev) => ({ ...prev, [target.id]: t('admin.departments.deleteSuccess') }));
        setForceConfirm(null);
        load();
      })
      .catch((err) => {
        setRowError((prev) => ({
          ...prev,
          [target.id]: err instanceof Error ? err.message : t('common.error'),
        }));
        setForceConfirm(null);
      })
      .finally(() => setDeletingId(null));
  };

  const closeForceConfirm = () => {
    if (deletingId) return;
    setForceConfirm(null);
  };

  return (
    <div className="page admin-departments-page">
      <h1>{t('admin.departments.title')}</h1>
      <p className="admin-page-intro">{t('admin.departments.intro')}</p>

      {/* 部署の追加・変更・削除は全社管理者だけ（#100 / BE PR #129）。拠点管理者は見るだけ */}
      {!editable && (
        <p className="admin-form-hint" data-testid="departments-readonly">
          {t('admin.departments.readOnly')}
        </p>
      )}
      {editable && (
      <form onSubmit={handleCreate} className="admin-card admin-form">
        <h3>{t('admin.departments.createTitle')}</h3>
        <div className="admin-form-group">
          <label htmlFor="admin-departments-nameLabel">{t('admin.departments.nameLabel')}</label>
          <input id="admin-departments-nameLabel"
            type="text"
            required
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            placeholder={t('admin.departments.namePlaceholder')}
            maxLength={100}
          />
        </div>
        <div className="admin-form-group">
          <label htmlFor="admin-departments-codeLabel">{t('admin.departments.codeLabel')}</label>
          <input id="admin-departments-codeLabel"
            type="text"
            value={createCode}
            onChange={(e) => setCreateCode(e.target.value)}
            placeholder={t('admin.departments.codePlaceholder')}
            maxLength={50}
          />
          <p className="admin-form-hint">{t('admin.departments.codeHint')}</p>
        </div>
        {createError && <p className="admin-form-error">{createError}</p>}
        {createMessage && <p className="admin-form-success">{createMessage}</p>}
        <button type="submit" className="admin-btn-primary" disabled={creating}>
          {creating ? t('common.loading') : t('admin.departments.createSubmit')}
        </button>
      </form>
      )}

      <div className="admin-card">
        <div className="admin-departments-list-header">
          <h3>{t('admin.departments.listTitle')}</h3>
          <label className="admin-departments-include-inactive">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            {t('admin.departments.includeInactive')}
          </label>
        </div>

        {error && <p className="admin-form-error">{error}</p>}
        {loading && <p>{t('common.loading')}</p>}
        {!loading && !error && (
          sortedList.length === 0 ? (
            <p className="admin-empty">{t('admin.departments.empty')}</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.departments.nameLabel')}</th>
                  <th scope="col">{t('admin.departments.codeLabel')}</th>
                  <th scope="col">{t('admin.departments.statusLabel')}</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {sortedList.map((row) => {
                  const edit =
                    editRows[row.id] ?? {
                      name: row.name,
                      code: row.code ?? '',
                      status: row.status,
                    };
                  const isInactive = row.status === 'inactive';
                  return (
                    <tr
                      key={row.id}
                      className={isInactive ? 'admin-departments-row-inactive' : ''}
                    >
                      <td>
                        <input
                          type="text"
                          value={edit.name}
                          disabled={!editable}
                          onChange={(e) =>
                            setEditRows((prev) => ({
                              ...prev,
                              [row.id]: { ...edit, name: e.target.value },
                            }))
                          }
                          maxLength={100}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={edit.code}
                          disabled={!editable}
                          onChange={(e) =>
                            setEditRows((prev) => ({
                              ...prev,
                              [row.id]: { ...edit, code: e.target.value },
                            }))
                          }
                          placeholder={t('admin.departments.codePlaceholder')}
                          maxLength={50}
                        />
                      </td>
                      <td>
                        <span
                          className={`settings-badge admin-departments-status-badge admin-departments-status-badge--${row.status}`}
                        >
                          {t(`admin.departments.statusBadge.${row.status}`, {
                            defaultValue: row.status,
                          })}
                        </span>
                        <select
                          className="admin-departments-status-select"
                          value={edit.status}
                          disabled={!editable}
                          onChange={(e) =>
                            setEditRows((prev) => ({
                              ...prev,
                              [row.id]: { ...edit, status: e.target.value },
                            }))
                          }
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {t(`admin.departments.statusBadge.${s}`, { defaultValue: s })}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {editable && (
                        <div className="admin-departments-actions">
                          <button
                            type="button"
                            className="admin-btn-text"
                            onClick={() => handleUpdate(row)}
                            disabled={savingId === row.id}
                          >
                            {savingId === row.id
                              ? t('common.loading')
                              : t('admin.departments.editAction')}
                          </button>
                          <button
                            type="button"
                            className="admin-btn-text admin-btn-text-danger"
                            onClick={() => handleDelete(row)}
                            disabled={deletingId === row.id}
                          >
                            {deletingId === row.id
                              ? t('common.loading')
                              : t('admin.departments.deleteAction')}
                          </button>
                        </div>
                        )}
                        {rowError[row.id] && (
                          <p className="admin-form-error admin-departments-row-error">
                            {rowError[row.id]}
                          </p>
                        )}
                        {rowNotice[row.id] && (
                          <p className="admin-form-success admin-departments-row-notice">
                            {rowNotice[row.id]}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </div>

      {forceConfirm && (
        <div className="admin-consent-docs-dialog-backdrop" role="dialog" aria-modal="true">
          <div className="admin-consent-docs-dialog">
            <h3>{t('admin.departments.forceDeleteDialog.title')}</h3>
            <p
              className="admin-consent-docs-dialog-warning admin-departments-has-users-warning"
              role="alert"
            >
              {forceConfirm.userCount != null
                ? t('admin.departments.hasUsersWarning', {
                    name: forceConfirm.target.name,
                    count: forceConfirm.userCount,
                  })
                : t('admin.departments.hasUsersWarningNoCount', {
                    name: forceConfirm.target.name,
                  })}
            </p>
            <p>{t('admin.departments.forceDeleteConfirm')}</p>
            <div className="admin-consent-docs-dialog-actions">
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={closeForceConfirm}
                disabled={deletingId === forceConfirm.target.id}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="admin-btn-danger"
                onClick={handleForceDelete}
                disabled={deletingId === forceConfirm.target.id}
              >
                {deletingId === forceConfirm.target.id
                  ? t('common.loading')
                  : t('admin.departments.forceDeleteSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="admin-back">
        <Link to="/admin">{t('common.back')} → {t('admin.backToDashboard')}</Link>
      </p>
    </div>
  );
}
